import { claude } from './api.js';
import { ST } from './state.js';

// ── DOCUMENT PARSERS ───────────────────────────────────────────────
/**
 * @param {string} url
 * @param {string} globalCheck
 * @returns {Promise<void>}
 */
async function loadLib(url, globalCheck) {
  if (window[globalCheck]) {
    return;
  }
  await new Promise((ok, fail) => {
    const s = document.createElement('script');
    s.src = url;
    const timer = setTimeout(() => {
      s.remove();
      fail(new Error(`Script load timeout: ${url}`));
    }, 15_000);
    s.onload = () => {
      clearTimeout(timer);
      ok();
    };
    s.onerror = () => {
      clearTimeout(timer);
      fail(new Error(`Failed to load: ${url}`));
    };
    document.head.appendChild(s);
  });
}

/**
 * @param {File} file
 * @returns {Promise<string>}
 */
async function parseDoc(file) {
  const ext = file.name.split('.').pop().toLowerCase();
  if (ext === 'md' || ext === 'txt') {
    return readAsText(file);
  }
  if (ext === 'csv') {
    return csvToMd(await readAsText(file));
  }
  if (ext === 'docx') {
    return docxToMd(file);
  }
  if (ext === 'pdf') {
    return pdfToMd(file);
  }
  if (ext === 'xlsx' || ext === 'xls') {
    return xlsxToMd(file);
  }
  throw new Error(`Unsupported file type: .${ext}`);
}

/**
 * @param {File} file
 * @returns {Promise<string>}
 */
function readAsText(file) {
  return new Promise((ok, fail) => {
    const r = new FileReader();
    r.onload = e => {
      ok(e.target.result);
    };
    r.onerror = fail;
    r.readAsText(file);
  });
}

/**
 * @param {File} file
 * @returns {Promise<string>}
 */
async function docxToMd(file) {
  await loadLib(
    'https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.8.0/mammoth.browser.min.js',
    'mammoth',
  );
  const ab = await file.arrayBuffer();
  const result = await window.mammoth.convertToMarkdown({ arrayBuffer: ab });
  return result.value;
}

/**
 * @param {File} file
 * @returns {Promise<string>}
 */
async function pdfToMd(file) {
  if (!window.pdfjsLib) {
    await loadLib('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js', 'pdfjsLib');
    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
    }
  }
  const ab = await file.arrayBuffer();
  const pdf = await window.pdfjsLib.getDocument({ data: ab }).promise;
  let md = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const tc = await page.getTextContent();
    const pageText = tc.items.map(item => item.str).join(' ');
    md += (i > 1 ? '\n\n---\n\n' : '') + pageText;
  }
  return md.trim() || '*(PDF text could not be extracted — may be image-based)*';
}

/**
 * @param {File} file
 * @returns {Promise<string>}
 */
async function xlsxToMd(file) {
  await loadLib('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js', 'XLSX');
  const ab = await file.arrayBuffer();
  const wb = window.XLSX.read(ab, { type: 'array' });
  let md = '';
  wb.SheetNames.forEach(name => {
    const ws = wb.Sheets[name];
    const csv = window.XLSX.utils.sheet_to_csv(ws);
    md += `## ${name}\n\n${csvToMd(csv)}\n\n`;
  });
  return md.trim();
}

/**
 * @param {string} line
 * @returns {string[]}
 */
function parseCsvLine(line) {
  const cols = [];
  let cur = '',
    inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') {
        inQ = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQ = true;
    } else if (ch === ',') {
      cols.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  cols.push(cur.trim());
  return cols;
}

/**
 * @param {string} csv
 * @returns {string}
 */
function csvToMd(csv) {
  const lines = csv
    .trim()
    .split('\n')
    .filter(l => l.trim());
  if (!lines.length) {
    return '';
  }
  const rows = lines.map(l => parseCsvLine(l));
  const header = rows[0];
  let md = `| ${header.join(' | ')} |\n`;
  md += `| ${header.map(() => '---').join(' | ')} |\n`;
  rows.slice(1).forEach(r => {
    md += `| ${r.join(' | ')} |\n`;
  });
  return md;
}

/**
 * @param {string} name
 * @returns {{ ext: string, icon: string }}
 */
function docExt(name) {
  const ext = name.split('.').pop().toLowerCase();
  const icons = {
    docx: '📝',
    doc: '📝',
    pdf: '📕',
    xlsx: '📊',
    xls: '📊',
    csv: '📊',
    txt: '📄',
    md: '📄',
  };
  return { ext, icon: icons[ext] || '📄' };
}

// ── IMAGE ANALYSIS ─────────────────────────────────────────────────
// NOTE: Mixed concern -- depends on ST, claude(), renderImgGrid(). Extract to logic/ during Vue migration.
/** @returns {Promise<void>} */
async function analyseImages() {
  const pending = ST.uploads.images.filter(
    i => i.analysisStatus !== 'done' && i.analysisStatus !== 'analyzing',
  );
  if (!pending.length) {
    return;
  }
  pending.forEach(img => {
    img.analysisStatus = 'analyzing';
  });
  renderImgGrid();
  const sys = `<role>
You are a visual brand strategist analyzing uploaded images for use in a marketing website.
</role>

<task>
For each image, classify its type, suggest placement, and decide whether to use it.
Output a JSON array with one object per image.
Each object: {"filename":"...","type":"logo|hero|product|team|lifestyle|icon|screenshot|other","placement":"where on the landing page","useDecision":"use|replace-with-stock|replace-with-ai","reason":"brief reason"}
</task>`;
  const imgPayloads = pending.map(i => ({
    data: i.data, type: i.type, name: i.name,
    label: `Uploaded image: ${i.name} — analyze quality and placement suitability`,
  }));
  const userText = pending.map((img, i) => `Image ${i + 1}: ${img.name}`).join('\n');
  try {
    const raw = await claude({
      sys,
      user: userText,
      imgs: imgPayloads,
      tokens: 1200,
      effort: 'low',
    });
    const results = JSON.parse(raw);
    pending.forEach((img, i) => {
      if (results[i]) {
        img.analysis = results[i];
        img.analysisStatus = 'done';
      } else {
        img.analysis = null;
        img.analysisStatus = 'error';
      }
    });
  } catch (e) {
    console.error('analyseImages failed:', e);
    pending.forEach(img => {
      img.analysisStatus = 'error';
      img.analysis = null;
    });
  }
  renderImgGrid();
}

// ── MARKDOWN → HTML renderer (simple) ─────────────────────────────
/**
 * @param {string} block
 * @returns {string | null}
 */
function _mdTableToHtml(block) {
  const lines = block.trim().split('\n');
  if (lines.length < 2) {
    return null;
  }
  const esc = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const parsePipeRow = ln =>
    ln
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map(c => c.trim());
  // Check line 2 is a separator (| --- | --- |)
  if (!/^\|?[\s-:|]+\|?$/.test(lines[1])) {
    return null;
  }
  const headers = parsePipeRow(lines[0]);
  let html = '<table><thead><tr>';
  headers.forEach(h => {
    html += `<th>${esc(h)}</th>`;
  });
  html += '</tr></thead><tbody>';
  for (let r = 2; r < lines.length; r++) {
    if (!lines[r].includes('|')) {
      break;
    }
    const cols = parsePipeRow(lines[r]);
    html += '<tr>';
    cols.forEach(c => {
      html += `<td>${esc(c)}</td>`;
    });
    html += '</tr>';
  }
  html += '</tbody></table>';
  return html;
}

/**
 * @param {string} md
 * @returns {string}
 */
function mdToHtml(md) {
  if (!md) {
    return '';
  }
  // Extract and convert markdown tables before escaping HTML
  const tableRx = /((?:^\|.+\|[ \t]*\n){2,})/gm;
  const tables = [];
  md = md.replace(tableRx, match => {
    const html = _mdTableToHtml(match);
    if (html) {
      const placeholder = `\x00TABLE${tables.length}\x00`;
      tables.push(html);
      return placeholder;
    }
    return match;
  });

  let h = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // headings
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // bold / italic
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // hr
    .replace(/^---$/gm, '<hr>')
    // code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // lists (simple)
    .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    // paragraphs
    .replace(/\n\n/g, '</p><p>');
  // Wrap list items
  h = h.replace(/(<li>.*<\/li>)+/gs, m => `<ul>${m}</ul>`);
  // Restore tables
  tables.forEach((tbl, i) => {
    h = h.replace(`\x00TABLE${i}\x00`, tbl);
  });
  return `<p>${h}</p>`;
}

// ── MODULE EXPORTS ──────────────────────────────────────────────
export {
  _mdTableToHtml,
  analyseImages,
  csvToMd,
  docExt,
  docxToMd,
  loadLib,
  mdToHtml,
  parseCsvLine,
  parseDoc,
  pdfToMd,
  readAsText,
  xlsxToMd,
};
